import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import useImageCap from '@/hooks/useImageCap';
import DeletedImagePlaceholder, { isDeletedImageUrl } from './DeletedImagePlaceholder';

/**
 * Generate srcset paths from original path
 * @param {string} original - Original file path (e.g., images/2024/photo-abc-original.jpg)
 * @returns {Object} srcset object with width keys
 */
function generateSrcsetFromOriginal(original) {
    if (!original) return null;
    // images/2024/photo-abc-original.jpg → images/2024/photo-abc
    const basePath = original.replace(/-original\.[^.]+$/, '');
    if (basePath === original) return null; // Pattern didn't match
    return {
        480: `${basePath}-480.webp`,
        768: `${basePath}-768.webp`,
        1280: `${basePath}-1280.webp`,
        1920: `${basePath}-1920.webp`,
    };
}

/**
 * Helper function to get srcset from file object
 * Supports both new srcset structure and legacy lg/md/sm URLs
 * Note: Derived srcset may include sizes that don't exist - use with getAvailableSizes() to filter
 * @param {Object} file - File object from database
 * @returns {Object} srcset object with width keys
 */
export function getImageSrcset(file) {
    // New structure: url.srcset (preferred - only contains files that exist)
    // If srcset is an empty object, it means files haven't been generated yet
    // In that case, we should not derive srcset to avoid including non-existent files
    if (file?.url?.srcset) {
        const srcsetKeys = Object.keys(file.url.srcset);
        if (srcsetKeys.length > 0) {
            return file.url.srcset;
        }
        // Explicit empty srcset means variants are still being generated.
        // Return null so callers fall back to `url.original` instead of
        // constructing URLs for files that do not exist yet.
        return null;
    }
    
    // Try to derive WebP paths from original filename
    // This works for images that have been regenerated but DB not updated
    // However, this may include sizes that don't exist, so should be filtered by getAvailableSizes()
    const derivedSrcset = generateSrcsetFromOriginal(file?.url?.original);
    if (derivedSrcset) {
        return derivedSrcset;
    }
    
    // Legacy fallback: map old sizes to new widths
    // Use WebP versions if available, fallback to JPEG
    return {
        480: file?.webp?.sm || file?.url?.sm,
        768: file?.webp?.md || file?.url?.md,
        1280: file?.webp?.lg || file?.url?.lg,
        1920: file?.webp?.lg || file?.url?.lg, // Use lg for 1920 as well
    };
}

/**
 * Get available sizes based on original image dimensions
 * Sizes larger than the original width are not generated (no upscaling)
 * @param {number|null} originalWidth - Original image width in pixels
 * @returns {number[]} Array of available widths, sorted descending
 */
export function getAvailableSizes(originalWidth) {
    const ALL_SIZES = [3000, 1920, 1280, 768, 480];
    if (!originalWidth) return ALL_SIZES; // If unknown, assume all available
    return ALL_SIZES.filter(size => size <= originalWidth);
}

/**
 * Get the best available URL for a requested size
 * Falls back to smaller sizes if the requested size wasn't generated
 * Only returns URLs for sizes that are actually available (not larger than original)
 * @param {Object} file - File object from database
 * @param {number} requestedWidth - Requested width (480, 768, 1280, or 1920)
 * @returns {string|null} URL to the best available image
 */
export function getBestAvailableUrl(file, requestedWidth = 1280) {
    const srcset = getImageSrcset(file);
    const originalWidth = file?.dimensions?.width;
    const availableSizes = getAvailableSizes(originalWidth);
    
    // Filter srcset to only include sizes that are actually available
    // This prevents returning URLs for sizes that don't exist (e.g., 1920 for a 1200px image)
    const filteredSrcset = {};
    for (const size of availableSizes) {
        if (srcset?.[size]) {
            filteredSrcset[size] = srcset[size];
        }
    }
    
    // If no filtered srcset entries, fall back to original
    if (Object.keys(filteredSrcset).length === 0) {
        return file?.url?.original || null;
    }
    
    // Try requested size first, then fall back to smaller available sizes
    const sizesToTry = [requestedWidth, ...availableSizes.filter(s => s < requestedWidth)];
    
    for (const size of sizesToTry) {
        if (filteredSrcset[size]) {
            return filteredSrcset[size];
        }
    }
    
    // Final fallback: largest available size from filtered srcset
    const sortedSizes = availableSizes.sort((a, b) => b - a);
    for (const size of sortedSizes) {
        if (filteredSrcset[size]) {
            return filteredSrcset[size];
        }
    }
    
    // Ultimate fallback: original
    return file?.url?.original || null;
}

/**
 * Helper function to get default src (1280px variant with fallbacks)
 * @param {Object} file - File object from database
 * @returns {string} URL to default image
 */
export function getDefaultSrc(file) {
    return getBestAvailableUrl(file, 1280) || file?.url?.original;
}

/**
 * Build srcset string from srcset object, filtering by available sizes
 * Only includes sizes that are actually available (not larger than original)
 * @param {Object} srcset - Object with width keys and URL values
 * @param {number|null} originalWidth - Original image width to filter out non-existent sizes
 * @param {number} [maxWidth] - Widest candidate to offer (slow links). The smallest
 *   candidate is always kept so the attribute never ends up empty.
 * @returns {string} srcset attribute value
 */
export function buildSrcsetString(srcset, originalWidth = null, maxWidth = undefined) {
    if (!srcset) return '';
    
    const availableSizes = getAvailableSizes(originalWidth);
    
    const entries = Object.entries(srcset)
        .filter(([width, url]) => {
            const widthNum = parseInt(width, 10);
            // Filter out undefined/null URLs
            if (!url) return false;
            // Filter out sizes larger than original (they weren't generated)
            // This is critical to prevent 404s for non-existent files
            if (originalWidth && widthNum > originalWidth) return false;
            // Only include sizes that are in the available sizes list
            // This ensures we don't include sizes that shouldn't exist
            if (!availableSizes.includes(widthNum)) return false;
            return true;
        });
    const capped = Number.isFinite(maxWidth)
        ? entries.filter(([width]) => parseInt(width, 10) <= maxWidth)
        : entries;
    const smallest = entries.length
        ? [entries.reduce((a, b) => (parseInt(a[0], 10) <= parseInt(b[0], 10) ? a : b))]
        : [];
    return (capped.length ? capped : smallest)
        .map(([width, url]) => {
            const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
            return `${normalizedUrl} ${width}w`;
        })
        .join(', ');
}

/**
 * OptimizedImage Component
 * 
 * Renders responsive images with srcset and sizes attributes.
 * Supports both new srcset structure and legacy URL format.
 * Forwards ref to the inner img element.
 */
const OptimizedImage = forwardRef(function OptimizedImage({
    src,
    srcset,
    alt,
    width,
    height,
    originalWidth,  // Original image width - used to filter out non-existent larger sizes
    maxWidth,
    fill = false,
    sizes,
    quality = 75,
    priority = false,
    className = '',
    style = {},
    onLoad,
    onError,
    ...rest
}, ref) {
    const linkCap = useImageCap();
    const [failedSrc, setFailedSrc] = useState(null);
    const cap = maxWidth ?? linkCap;
    // Build srcset string if srcset object provided, filtering by original dimensions
    const srcsetStr = srcset ? buildSrcsetString(srcset, originalWidth, cap) : '';
    
    // Normalize src to ensure leading slash
    const normalizedSrc = src?.startsWith('/') ? src : `/${src}`;
    
    // Default sizes if not provided
    const defaultSizes = '(max-width: 768px) 100vw, 70vw';
    
    // For fill mode, use absolute positioning
    const imgStyle = fill
        ? {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            ...style,
        }
        : {
            maxWidth: '100%',
            height: 'auto',
            ...style,
        };

    const removed = isDeletedImageUrl(src);
    if (removed || failedSrc === normalizedSrc) {
        return (
            <DeletedImagePlaceholder
                thumbnailUrl={removed ? src : null}
                label={removed ? undefined : 'Image unavailable'}
                width={width || null}
                height={height || null}
                fill={fill}
                className={className}
                style={fill ? style : { ...style, height: undefined }}
            />
        );
    }

    return (
        <img
            ref={ref}
            src={normalizedSrc}
            srcSet={srcsetStr || undefined}
            sizes={srcsetStr ? (sizes || defaultSizes) : undefined}
            alt={alt}
            width={!fill ? width : undefined}
            height={!fill ? height : undefined}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            className={className}
            style={imgStyle}
            onLoad={onLoad}
            onError={(event) => {
                setFailedSrc(normalizedSrc);
                onError?.(event);
            }}
            {...rest}
        />
    );
});

OptimizedImage.propTypes = {
    src: PropTypes.string.isRequired,
    srcset: PropTypes.object,
    alt: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    originalWidth: PropTypes.number,  // Original image width for filtering srcset
    maxWidth: PropTypes.number,  // Widest srcset candidate; defaults to the link-speed cap
    fill: PropTypes.bool,
    sizes: PropTypes.string,
    quality: PropTypes.number,
    priority: PropTypes.bool,
    className: PropTypes.string,
    style: PropTypes.object,
    onLoad: PropTypes.func,
    onError: PropTypes.func,
};

export default OptimizedImage;
